/**
 * Organization row shapes.
 *
 * These are SNAKE_CASE on purpose. The organization controllers return raw
 * database rows through the generic CRUD service, so the wire field names are
 * the column names exactly. Declaring them in camelCase would typecheck
 * perfectly and render a table of em-dashes, which is the failure mode this
 * project keeps producing — the shape was verified against the running API
 * before these were written, not inferred from the spec.
 */
import { countryName } from "@/lib/reference";

export interface LegalEntityRow {
  id: string;
  name: string;
  legal_name: string;
  tax_id: string | null;
  registration_country: string;
  currency_code: string;
  status: "Provisional" | "Active" | "Inactive";
  created_at: string;
}

export interface CompanyRow {
  id: string;
  legal_entity_id: string;
  name: string;
  code: string;
  status: "Draft" | "Active" | "Closed";
  created_at: string;
}

export interface BranchRow {
  id: string;
  company_id: string;
  name: string;
  code: string;
  timezone: string | null;
  address: Record<string, unknown> | null;
  status: string;
  created_at: string;
}

export interface DepartmentRow {
  id: string;
  company_id: string;
  parent_department_id: string | null;
  name: string;
  code: string;
  cost_center_id: string | null;
  created_at: string;
}

export interface LocationRow {
  id: string;
  branch_id: string | null;
  warehouse_id: string | null;
  name: string;
  code: string;
  address: Record<string, unknown> | null;
  status: string;
  created_at: string;
}

/**
 * Render a jsonb address column as one line, or a dash when it is empty. The
 * country is named in `language` (the interface's), not shown as its ISO
 * code; the separator is the language's («، » in Arabic).
 */
export function addressLine(
  address: Record<string, unknown> | null | undefined,
  language = "en",
): string {
  if (!address) return "—";
  const parts = ["street", "street2", "city", "region", "postalCode"]
    .map((k) => address[k])
    .filter((v): v is string => typeof v === "string" && v.trim().length > 0);
  const country = address.countryCode;
  if (typeof country === "string" && /^[A-Za-z]{2}$/.test(country))
    parts.push(countryName(country, language));
  return parts.length > 0 ? parts.join(language === "ar" ? "، " : ", ") : "—";
}
